import React, {useCallback} from 'react';
import {Helmet} from 'react-helmet';
import {useDispatch} from 'react-redux';
import {FlexItem} from '../../../../CommonStyleUtils/dwebCommon.styles';
import * as S from './CaptivateBannerGalleryWithVideo.styles';

// ICONS
import HappyAngleRightIconNew from '@stencil/icons/src/HappyAngleRightIconNew/HappyAngleRightIconNew';
import HappyAngleLeftIconNew from '@stencil/icons/src/HappyAngleLeftIconNew/HappyAngleLeftIconNew';
import HappyLeftArrowTransparent from '@stencil/icons/src/HappyLeftArrowTransparent/HappyLeftArrowTransparent';
import HappyNewImagePropertyIcon from '@stencil/icons/src/HappyNewImagePropertyIcon/HappyNewImagePropertyIcon';

// COMPONENTS
import useCarousel from '@stencil/hook-use-carousel/src/useCarousel';
import ImageCategory from './ImageCategory';

// Omniture Imports
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';
import Carousel from '../../../common/Carousel/Carousel';

interface CaptivateBannerGalleryWithVideoProps {
  captivateImages: Array<{url: string; big: string; mediaType: string}>;
  hotellierImagesCount: number;
  isSeoPage: boolean;
  hotelName: string;
  categories: Array<{name: string; count: number}>;
  onHotelierImageClick: (category: string) => void;
}

const CaptivateBannerGalleryWithVideo: React.FC<CaptivateBannerGalleryWithVideoProps> = ({
  captivateImages = [],
  hotellierImagesCount = 0,
  isSeoPage = false,
  hotelName = '',
  categories = [],
  onHotelierImageClick,
}) => {
  const handleImageClick = useCallback(
    e => {
      e.stopPropagation();
      onHotelierImageClick('hotellier');
    },
    [onHotelierImageClick],
  );
  const dispatch = useDispatch();

  const onPreviousIconClick = useCallback(() => {
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.GalleryWithVideoV2}_${detailsClick.captivateGalleryLabel.left_arrow}`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.GalleryWithVideoV2,
          destination: '',
        },
      }),
    );
  }, [dispatch]);

  const onNextIconClick = useCallback(() => {
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.GalleryWithVideoV2}_${detailsClick.captivateGalleryLabel.right_arrow}`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.GalleryWithVideoV2,
          destination: '',
        },
      }),
    );
  }, [dispatch]);

  const clickHandler = useCallback((onClick, afterClick) => {
    return (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick(e);
      afterClick();
    };
  }, []);

  const renderIcon = (Icon: React.ElementType, afterClick: () => void) => {
    return ({
      onClick,
      isDisabled,
    }: {
      onClick: (e: React.MouseEvent) => void;
      isDisabled: boolean;
    }) => {
      if (isDisabled) return <div />;
      return (
        <S.IconWrapper onClick={clickHandler(onClick, afterClick)}>
          <Icon fill="#FFFFFF" width="28px" height="32px" />
        </S.IconWrapper>
      );
    };
  };

  const renderItem = (
    item: {url: string; big: string; mediaType: string},
    index: number,
    isLastItem: boolean,
  ) => {
    if (item?.mediaType?.toLowerCase() === 'video') return null;
    const conditionalProps: {itemProp?: 'image'} = index === 0 ? {itemProp: 'image'} : {};
    if (isLastItem) {
      return (
        <S.WideImageViewAllPhotosOuter key={index}>
          <S.CaptivateGalleryImageViewAll isSeoPage={isSeoPage} src={item?.url} />
          <S.WideImageViewAllPhotos onClick={handleImageClick}>
            <S.ViewAllPhotosText>
              <span>View All</span>
            </S.ViewAllPhotosText>
          </S.WideImageViewAllPhotos>
        </S.WideImageViewAllPhotosOuter>
      );
    }
    return index === 0 ? (
      <S.CaptivateGalleryImageBasic
        src={item?.url}
        isSeoPage={isSeoPage}
        alt={hotelName}
        key={item.url || index}
        listItemWidth="100%"
        {...conditionalProps}
        onClick={handleImageClick}
        rel="preload"
      />
    ) : (
      <S.CaptivateGalleryImage
        src={item?.url}
        isSeoPage={isSeoPage}
        alt="Main gallery"
        key={item.url || index}
        listItemWidth="100%"
        {...conditionalProps}
        onClick={handleImageClick}
      />
    );
  };

  if (!captivateImages.length) {
    return (
      <S.CaptivateBannerGallery>
        <S.CaptivateMainImageWrapper>
          <HappyNewImagePropertyIcon width="590px" height="324px" margin="0 10px 0 0" />
        </S.CaptivateMainImageWrapper>
        <S.ImageRightSectionWrapper width={'232px'} />
      </S.CaptivateBannerGallery>
    );
  }

  const firstImage = captivateImages?.[0]?.url;

  return (
    <S.CaptivateBannerGallery>
      {firstImage && (
        <Helmet>
          <link rel="preload" as="image" href={firstImage} />
        </Helmet>
      )}
      <S.CaptivateMainImageWrapper isSeoPage={isSeoPage}>
        <Carousel
          preloadImagesOnInteraction
          data={captivateImages}
          renderItem={renderItem}
          renderLeftIcon={renderIcon(HappyAngleLeftIconNew, onPreviousIconClick)}
          renderRightIcon={renderIcon(HappyAngleRightIconNew, onNextIconClick)}
          onClickControlPanel={handleImageClick}
        />

        <S.ItemInfoWrapperLarge onClick={handleImageClick}>
          <S.InfoLeftSection>
            <p>
              <strong>Property Photos ({hotellierImagesCount})</strong>
            </p>
          </S.InfoLeftSection>
          <S.InfoRightSection>
            View All <HappyLeftArrowTransparent />
          </S.InfoRightSection>
        </S.ItemInfoWrapperLarge>
      </S.CaptivateMainImageWrapper>
      {categories.length > 0 && <ImageCategory categories={categories} isSeoPage={isSeoPage} />}
    </S.CaptivateBannerGallery>
  );
};

export default CaptivateBannerGalleryWithVideo;
